import { motion } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Heart, Target, Users, Star,
  ArrowRight, Stethoscope,
  Activity, Footprints, Video,
} from 'lucide-react';
import { paiPaziente, profiloPaziente } from '@/data/mockPatientData';

/* ══════ Icone interventi ══════ */
const interventoIcons = {
  nurse: Activity,
  physio: Footprints,
  oss: Heart,
  teleconsulto: Video,
};

/* ══════ CONTENUTO OBIETTIVI ══════ */
function ObiettiviContent() {
  const pai = paiPaziente;

  return (
    <div className="space-y-5" data-testid="obiettivi-content">
      {/* Intestazione PAI */}
      <Card className="border-0 shadow-[0_2px_12px_rgba(14,165,233,0.08)] rounded-3xl overflow-hidden bg-gradient-to-br from-sky-50 via-white to-teal-50 dark:from-sky-950/20 dark:via-card dark:to-teal-950/10" data-testid="pai-header-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-2xl bg-sky-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-sky-500 uppercase tracking-wider">Il tuo piano di cure attivo</p>
              <p className="text-base font-bold text-foreground">Attivo dal {pai.dataInizio} al {pai.dataFine}</p>
            </div>
            <Badge className="ml-auto rounded-full px-3.5 py-1 border-0 bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="pai-stato-badge">Attivo</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Obiettivo Principale */}
      <Card className="border-0 shadow-[0_1px_6px_rgba(0,0,0,0.05)] rounded-3xl" data-testid="obiettivo-principale">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Star className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-amber-500 uppercase tracking-wider mb-1">Il tuo obiettivo</p>
              <p className="text-lg font-bold text-foreground leading-snug">{pai.obiettivoPrincipale}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Equipe */}
      <div className="space-y-3" data-testid="equipe-section">
        <p className="text-sm font-bold text-foreground px-1 flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />Il tuo team dedicato
        </p>
        <div className="flex flex-wrap gap-3">
          {pai.equipe.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 + i * 0.05 }}>
              <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)] rounded-3xl" data-testid={`equipe-${m.id}`}>
                <CardContent className="p-4 flex items-center gap-3 min-w-[180px]">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: m.color }}>
                    {m.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">{m.nome}</p>
                    <p className="text-[11px] text-muted-foreground">{m.ruolo}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Interventi Previsti */}
      <div className="space-y-3" data-testid="interventi-section">
        <p className="text-sm font-bold text-foreground px-1 flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-muted-foreground" />Cosa prevede il tuo piano
        </p>
        {pai.interventi.map((int, i) => {
          const Icon = interventoIcons[int.icona] || Activity;
          return (
            <motion.div key={int.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)] rounded-3xl" data-testid={`intervento-${int.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-foreground">{int.testo}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{int.dettaglio}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground/30 flex-shrink-0" />
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════ PAGINA PRINCIPALE ══════ */
export default function PatientCarePlan() {
  return (
    <AppLayout role="paziente">
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="patient-care-plan">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center">
            <Heart className="w-5 h-5 text-sky-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">Il mio Percorso di Cura</h1>
            <p className="text-sm text-muted-foreground">Obiettivi e Team — {profiloPaziente.nome} {profiloPaziente.cognome}</p>
          </div>
        </div>

        <ObiettiviContent />
      </div>
    </AppLayout>
  );
}
